import type { NextPage } from 'next';
import styles from './EventComponent.module.scss';
import { Event } from 'types/event';
import { usePathname, useRouter } from 'next/navigation';
import AttendEventComponent from 'componentsUnits/attendEventComponent';
import { Activity } from 'types/user';
import EntryComponentContainer from '../../entryComponentContainer';
import { getDay, getMonth, getRecurringStartDate, isEventLiked } from 'helpers/event';
import { flagPost } from 'actions/post';
import { useState, useEffect, useMemo } from 'react';
import { Option } from 'types/options';
import { useAppDispatch, useAppSelector } from 'reducers/storeHooks';
import { generateExcerpt, handleSharingUsingCapacitor } from 'helpers/general';
import EventImageComponent from 'componentsUnits/eventImageComponent';
import RowComponent from 'componentsUnits/row';
import { getEventUrl } from 'helpers/navigation';
import { deleteEvent } from 'actions/event';
import ColumnComponent from 'componentsUnits/columnComponent';
import { likeEvent } from 'actions/activity';
import { BASE_PATH } from 'actions/constants';
import { updateToken } from 'actions/user';

interface Props {
  event: Event;
  entryId: string;
  activity?: Activity;
  showorganiser?: boolean;
  forceDate?: number;
  showExcerpt?: boolean;
  hideImageDate?: boolean;
}

const EventComponent: NextPage<Props> = ({
  event,
  entryId,
  activity,
  showorganiser,
  forceDate,
  showExcerpt,
  hideImageDate,
}) => {
  const router = useRouter();
  const uid = useAppSelector((state) => state.userReducer.user?.uid);
  const dispatch = useAppDispatch();
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [options, setOptions] = useState<Option[]>([]);
  const pathname = usePathname();

  const goToProfile = (uid: string) => {
    router.push(`/profile?uid=${uid}`);
  };

  const goToEvent = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    router.push(getEventUrl(entryId));
  };

  const deleteEventClicked = (entryId: string) => {
    if (confirm("Are you sure you want to delete this event?")) {
      dispatch(deleteEvent(entryId));
    }
  };

  const isLiked = useMemo(() => {
    return activity && isEventLiked(entryId, activity)
  }, [activity])

  useEffect(() => {
    const startDateRecurring = forceDate ?? getRecurringStartDate(event, Date.now().toString());
    setDay(getDay(startDateRecurring));
    setMonth(getMonth(startDateRecurring));
  }, [event, forceDate]);

  useEffect(() => {
    if (event?.uid === uid) {
      setOptions([
        {
          label: "Delete",
          onClick: () => deleteEventClicked(entryId),
        },
        {
          label: "Edit",
          onClick: () => router.push(`/event/edit?eid=${event.id}`),
        },
      ]);
    } else {
      setOptions([
        {
          label: "Flag",
          onClick: () => {
            if (confirm("Are you sure you want to flag this event?")) {
              dispatch(flagPost(entryId));
            }
          },
        },
        {
          label: isLiked ? "Remove Like" : "Like",
          onClick: async () => {
            await dispatch(updateToken());
            dispatch(likeEvent(entryId, !isLiked))
          }
        },
        {
          label: "Share",
          onClick: () => {
            handleSharingUsingCapacitor(`${BASE_PATH + pathname}?eid=${event.id}`, event.title);
          }
        }
      ]);
    }
  }, [event, uid, entryId, dispatch, router, isLiked]);

  return (
    <EntryComponentContainer type="EVENT" options={options}>
      <>
        <EventImageComponent
          onClick={goToEvent}
          image={event.image}
          day={day}
          month={month}
          hideDate={hideImageDate}
          buttons={
            <RowComponent center noPadding gap>
              {activity && (
                <AttendEventComponent
                  eventTitle={event.title}
                  eventDescription={event.description}
                  startDate={new Date(event.startDate)}
                  startTime={event.startTime}
                  activity={activity}
                  eventId={entryId}
                />
              )}
            </RowComponent>
          }
        />
        <div className={styles.infoWrapper}>
          <div className={styles.left_side}>
            <div className={styles.title}>{event.title}</div>
            {showorganiser && (
              <div className={styles.subtitle} onClick={() => goToProfile(event.organiserId)}>
                Check organiser
              </div>
            )}
            <div className={styles.interests_wrapper}>
              <div className={styles.interests}>{event.attendsIds ? event.attendsIds.length : 0}+</div>
            </div>
          </div>
          <div className={styles.right_side}>
            <div className={styles.date}>
              <div className={styles.day}>{day}</div>
              <div className={styles.month}>{month}</div>
            </div>
          </div>
        </div>
        <ColumnComponent fullWidth padding>
          {showExcerpt ? (
            <div className={styles.subtitle}>{generateExcerpt(event.description)}</div>
          ) : (
            <div className={styles.subtitle}>{event.description}</div>
          )}
        </ColumnComponent>
      </>
    </EntryComponentContainer>
  );
};

export default EventComponent;
